/**
 * Preferences › Voice › Shortcut and prompts: the start/stop shortcut and the
 * prompts a stop applies. The shortcut stops with Default (its switch off
 * delivers the text as spoken); Command+N while listening stops with numbered
 * prompt N instead. Each row shows its key, so the page explains the
 * shortcut. Numbers follow list order, nine at most.
 */
import type { ReactNode } from "react";
import { Icons, PocketRow, PocketSection, ToggleSwitch } from "@/components/ui";
import { MAX_PROMPTS } from "@/desktop/dictation-anywhere/polish-prompts";
import { useT } from "@/i18n";
import { settingsVoiceMessages } from "@/i18n/messages/settings-voice";
import {
  SHORTCUT_LABEL_KEY,
  settingsShortcutsMessages,
} from "@/i18n/messages/settings-shortcuts";
import { useDictationAnywhereSettingsStore } from "@/stores/dictation-anywhere-settings-store";
import { shortcutNeedsKey, useShortcutStore } from "@/stores/shortcut-store";
import { useSettingsNav } from "./settings-navigation";
import {
  DictationShortcutCaps,
  PromptKeyCaps,
} from "./voice-dictation-key-caps";
import {
  DICTATION_SHORTCUT_ID,
  promptChipsSummary,
  promptKeyModifier,
  promptLabel,
  startStopPhrase,
  useDictationShortcutBinding,
} from "./voice-dictation-keys";

/** The editor's `promptId` for Default. */
export const DEFAULT_PROMPT_ID = "default";

/** The key column in front of each prompt row. */
function KeyColumn({ children }: { children?: ReactNode }) {
  return <span className="flex w-[46px] items-center">{children}</span>;
}

export function VoiceDictationPromptsPage() {
  const nav = useSettingsNav();
  const t = useT(settingsVoiceMessages);
  const tShortcuts = useT(settingsShortcutsMessages);
  useDictationShortcutBinding();
  const needsKey = useShortcutStore((state) =>
    shortcutNeedsKey(state, DICTATION_SHORTCUT_ID),
  );
  const defaultPrompt = useDictationAnywhereSettingsStore(
    (state) => state.defaultPrompt,
  );
  const prompts = useDictationAnywhereSettingsStore((state) => state.prompts);
  const setDefaultEnabled = useDictationAnywhereSettingsStore(
    (state) => state.setDefaultEnabled,
  );
  const addPrompt = useDictationAnywhereSettingsStore(
    (state) => state.addPrompt,
  );

  function openEditor(promptId: string, title: string) {
    nav.push({
      page: "preferences-voice-prompt-edit",
      args: { promptId, title },
    });
  }

  function add() {
    const id = addPrompt();
    if (id) openEditor(id, promptLabel({ name: "" }, prompts.length));
  }

  return (
    <div data-testid="settings-page-preferences-voice-prompts">
      <PocketSection>
        <PocketRow
          testId="voice-prompts-start-stop"
          title={t("startAndStop")}
          secondLine={needsKey ? t("keyInUse") : undefined}
          status={needsKey ? { tone: "question" } : undefined}
          trailing={needsKey ? undefined : <DictationShortcutCaps />}
          chevron
          onSelect={() =>
            nav.push({
              page: "preferences-shortcut-edit",
              args: {
                shortcutId: DICTATION_SHORTCUT_ID,
                title: tShortcuts(SHORTCUT_LABEL_KEY[DICTATION_SHORTCUT_ID]),
              },
            })
          }
        />
      </PocketSection>
      <PocketSection title={t("stopWith")}>
        <PocketRow
          testId="voice-prompt-row-default"
          leading={
            <KeyColumn>{needsKey ? null : <DictationShortcutCaps />}</KeyColumn>
          }
          title={t("promptDefault")}
          secondLine={
            defaultPrompt.enabled
              ? promptChipsSummary(defaultPrompt.instruction)
              : t("asSpokenNoAi")
          }
          onSelect={() => openEditor(DEFAULT_PROMPT_ID, t("promptDefault"))}
          trailing={
            <ToggleSwitch
              checked={defaultPrompt.enabled}
              onCheckedChange={setDefaultEnabled}
              aria-label={t("rewriteWithAi")}
              data-testid="voice-prompt-default-toggle"
            />
          }
        />
        {prompts.map((prompt, index) => {
          const name = promptLabel(prompt, index);
          return (
            <PocketRow
              key={prompt.id}
              testId={`voice-prompt-row-${index + 1}`}
              leading={
                <KeyColumn>
                  <PromptKeyCaps number={index + 1} />
                </KeyColumn>
              }
              title={name}
              secondLine={promptChipsSummary(prompt.instruction)}
              chevron
              onSelect={() => openEditor(prompt.id, name)}
            />
          );
        })}
        {prompts.length < MAX_PROMPTS ? (
          <PocketRow
            testId="voice-prompt-add"
            leading={
              <KeyColumn>
                <Icons.Plus className="h-4 w-4" aria-hidden="true" />
              </KeyColumn>
            }
            title={t("addPrompt")}
            onSelect={add}
          />
        ) : null}
        <p
          className="p-2 text-appkit-small text-muted"
          data-testid="voice-prompts-footnote"
        >
          {t("promptsFootnote", {
            start: startStopPhrase(),
            modifier: promptKeyModifier(),
          })}
        </p>
      </PocketSection>
    </div>
  );
}
